/**
 * MinistrareIA - WhatsApp Business Service
 * Service for WhatsApp Business API (Meta Cloud API) communication
 */

import api from '../global/api';

const WA_BUSINESS_BASE = '/api/wa-business';

/**
 * WhatsApp Business Service Class
 */
class WABusinessService {
  // ============================================
  // CONNECTION / ACCOUNT
  // ============================================

  /**
   * Get connection status
   * @returns {Promise<Object>} { connected, status, phone_number, verified_name, token_expired }
   */
  async getStatus() {
    const response = await api.get(`${WA_BUSINESS_BASE}/status`);
    return response.data;
  }

  /**
   * Get account details
   * @returns {Promise<Object>} Account data
   */
  async getAccount() {
    const response = await api.get(`${WA_BUSINESS_BASE}/account`);
    return response.data;
  }

  /**
   * Exchange OAuth code for token and configure account
   * @param {string} code - Authorization code from Meta
   * @param {string} wabaId - WhatsApp Business Account ID
   * @param {string} phoneNumberId - Phone Number ID
   * @param {string} businessId - Meta Business ID
   * @returns {Promise<Object>} Account configuration result
   */
  async exchangeOAuth(code, wabaId, phoneNumberId, businessId) {
    const response = await api.post(`${WA_BUSINESS_BASE}/oauth/exchange`, {
      code,
      waba_id: wabaId,
      phone_number_id: phoneNumberId,
      business_id: businessId,
    });
    return response.data;
  }

  /**
   * Disconnect WhatsApp Business account
   * @returns {Promise<Object>} Disconnection result
   */
  async disconnect() {
    const response = await api.delete(`${WA_BUSINESS_BASE}/account`);
    return response.data;
  }

  // ============================================
  // CHATS
  // ============================================

  /**
   * List all chats
   * @param {number} limit - Max chats to return
   * @param {number} offset - Pagination offset
   * @returns {Promise<Object>} { data: Chat[], count: number }
   */
  async getChats(limit = 50, offset = 0) {
    const response = await api.get(
      `${WA_BUSINESS_BASE}/chats?limit=${limit}&offset=${offset}`
    );
    return response.data;
  }

  /**
   * Get a specific chat
   * @param {string} chatId - Chat UUID
   * @returns {Promise<Object>} Chat data
   */
  async getChat(chatId) {
    const response = await api.get(`${WA_BUSINESS_BASE}/chats/${chatId}`);
    return response.data;
  }

  /**
   * Mark chat as read
   * @param {string} chatId - Chat UUID
   * @returns {Promise<Object>} Result
   */
  async markAsRead(chatId) {
    const response = await api.post(`${WA_BUSINESS_BASE}/chats/${chatId}/read`);
    return response.data;
  }

  // ============================================
  // MESSAGES
  // ============================================

  /**
   * Get messages from a chat
   * @param {string} chatId - Chat UUID
   * @param {number} limit - Max messages to return
   * @param {number} offset - Pagination offset
   * @returns {Promise<Object>} { data: Message[], count: number }
   */
  async getMessages(chatId, limit = 50, offset = 0) {
    const response = await api.get(
      `${WA_BUSINESS_BASE}/chats/${chatId}/messages?limit=${limit}&offset=${offset}`
    );
    return response.data;
  }

  /**
   * Send text message to a chat
   * @param {string} chatId - Chat UUID
   * @param {string} message - Message text
   * @returns {Promise<Object>} { success, message_id, data }
   */
  async sendMessage(chatId, message) {
    const response = await api.post(
      `${WA_BUSINESS_BASE}/chats/${chatId}/messages`,
      { to: '', message } // 'to' is filled by backend from chat
    );
    return response.data;
  }

  /**
   * Send message directly to a phone number (creates chat if needed)
   * @param {string} to - Phone number
   * @param {string} message - Message text
   * @returns {Promise<Object>} { success, message_id, chat_id, data }
   */
  async sendMessageDirect(to, message) {
    const response = await api.post(`${WA_BUSINESS_BASE}/send`, {
      to: this.formatNumber(to),
      message,
    });
    return response.data;
  }

  /**
   * Send media message
   * @param {string} to - Phone number
   * @param {string} mediaUrl - Public URL of media
   * @param {string} mediaType - Type: image, video, audio, document
   * @param {string} caption - Optional caption
   * @param {string} filename - Optional filename for documents
   * @returns {Promise<Object>} { success, message_id, chat_id, data }
   */
  async sendMedia(to, mediaUrl, mediaType, caption = null, filename = null) {
    const response = await api.post(`${WA_BUSINESS_BASE}/send-media`, {
      to: this.formatNumber(to),
      media_url: mediaUrl,
      media_type: mediaType,
      caption,
      filename,
    });
    return response.data;
  }

  /**
   * Send message with multiple file attachments
   * Similar to Evolution API's sendMessageWithMedia
   * 
   * @param {string} to - Phone number
   * @param {string} text - Optional text message
   * @param {Array} files - Array of file objects
   * @returns {Promise<Object>} Result
   */
  async sendMessageWithMedia(to, text, files = []) {
    const hasText = text && text.trim();
    const hasFiles = files && files.length > 0;

    if (!hasText && !hasFiles) {
      throw new Error('Must provide text or files (or both)');
    }

    // If only text, use simple send
    if (hasText && !hasFiles) {
      return this.sendMessageDirect(to, text);
    }

    // If has files, send each file separately
    // WA Business API doesn't support batch file sending
    const results = [];
    
    // Send text first if present
    if (hasText) {
      try {
        const textResult = await this.sendMessageDirect(to, text);
        results.push({ type: 'text', success: true, data: textResult });
      } catch (err) {
        results.push({ type: 'text', success: false, error: err.message });
      }
    }

    // Send each file
    for (const file of files) {
      try {
        const mediaResult = await this.sendMedia(
          to,
          file.url,
          file.type || 'document',
          file.caption || null,
          file.filename || null
        );
        results.push({ type: 'media', success: true, data: mediaResult });
      } catch (err) {
        results.push({ type: 'media', success: false, error: err.message, file: file.filename });
      }
    }

    const allSuccess = results.every(r => r.success);
    const anySuccess = results.some(r => r.success);

    return {
      success: allSuccess,
      partial: anySuccess && !allSuccess,
      results,
      chat_id: results.find(r => r.data?.chat_id)?.data?.chat_id,
    };
  }

  // ============================================
  // UTILITIES
  // ============================================

  /**
   * Format phone number (remove special chars)
   * @param {string} number - Phone number
   * @returns {string} Cleaned number
   */
  formatNumber(number) {
    if (!number) return '';
    return number.replace(/[^0-9]/g, '');
  }

  /**
   * Extract phone number from various formats
   * @param {string} identifier - Phone or chat identifier
   * @returns {string} Phone number
   */
  extractNumber(identifier) {
    if (!identifier) return '';
    // WA Business uses plain phone numbers, not JIDs
    return this.formatNumber(identifier);
  }

  /**
   * Check if identifier is a group (WA Business doesn't support groups initially)
   * @param {string} identifier - Identifier
   * @returns {boolean} Always false for now
   */
  isGroup(identifier) {
    return false; // WA Business API doesn't support groups in initial version
  }

  /**
   * Get display name for a chat
   * @param {Object} chat - Chat object
   * @returns {string} Display name
   */
  getChatDisplayName(chat) {
    if (!chat) return 'Desconhecido';
    return chat.contact_name || chat.contact_phone || 'Desconhecido';
  }

  /**
   * Get message preview text
   * @param {Object} chat - Chat object
   * @returns {string} Preview text
   */
  getMessagePreview(chat) {
    if (!chat) return '';
    return chat.last_message_text || '';
  }

  /**
   * Format timestamp for display
   * @param {string} timestamp - ISO timestamp
   * @returns {string} Formatted time
   */
  formatTime(timestamp) {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Ontem';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('pt-BR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  }
}

// Export singleton instance
const waBusinessService = new WABusinessService();
export default waBusinessService;
